<script setup>
  import {computed, inject, nextTick, onUpdated, ref, useSlots} from "vue";

  import {MedButton, MedSpinner} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "ComponentDialog",
  });

  const props = defineProps({
    dialogTitle: {
      type: String,
      default: "",
    },
    dialogContent: {
      type: String,
      default: "",
    },
    dialogType: {
      type: String,
      default: "",
    },
    primaryLabel: {
      type: String,
      default: "OK",
    },
    secondaryLabel: {
      type: String,
      default: "",
    },
    cancelLabel: {
      type: String,
      default: "Abbrechen",
    },
    cancelButton: {
      type: Boolean,
      default: false,
    },
    dialogWidth: {
      type: String,
      default: "small",
    },
    dialogActive: {
      type: Boolean,
      default: false,
    },
    primaryButtonDisabled: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits([
    "dialogOpened",
    "dialogClosed",
    "primaryButtonClicked",
    "secondaryButtonClicked",
    "cancelButtonClicked",
    "okButtonClicked",
  ]);

  const slots = useSlots();

  const privacy = inject("$privacy");

  const refDialog = ref(null);

  const active = ref(false);

  const hasSlotContent = computed(() => !!slots.content);
  const hasSlotActions = computed(() => !!slots.actions);
  const widthGreaterThanSmall = computed(() => {
    return {
      "sm:w-96": props.dialogWidth === "small",
      "sm:w-164": props.dialogWidth === "medium",
      "sm:w-256": props.dialogWidth === "large",
    };
  });

  const handleCancel = () => {
    if (props.dialogType === "information") {
      close();
    }
  };

  onUpdated(() => {
    if (refDialog.value !== false) {
      privacy.decryptStaticContent(refDialog.value);
    }
  });

  const open = () => {
    active.value = true;

    nextTick(() => {
      if (!!refDialog.value === false) {
        return;
      }

      refDialog.value.showModal();
      addBackdropEventHandler();
      nextTick(() => {
        refDialog.value.scrollTo(0, 0);
      });
    });

    emit("dialogOpened");
  };

  const close = () => {
    if (active.value) {
      active.value = false;

      if (!!refDialog.value === false) {
        return;
      }

      refDialog.value.close();
      emit("dialogClosed");
      removeBackdropEventHandler();
    }
  };

  const primaryButtonClicked = () => {
    emit("primaryButtonClicked");
    close();
  };

  const secondaryButtonClicked = () => {
    emit("secondaryButtonClicked");
    close();
  };

  const cancelButtonClicked = () => {
    emit("cancelButtonClicked");
    close();
  };

  const okButtonClicked = () => {
    emit("okButtonClicked");
    close();
  };

  const addBackdropEventHandler = () => {
    if (props.dialogType === "information") {
      refDialog.value.addEventListener("click", backdropHandler);
    }
  };

  const removeBackdropEventHandler = () => {
    if (props.dialogType === "information") {
      refDialog.value.removeEventListener("click", backdropHandler);
    }
  };

  const backdropHandler = (event) => {
    const rect = refDialog.value.getBoundingClientRect();
    const isInDialog =
      rect.top <= event.clientY &&
      event.clientY <= rect.top + rect.height &&
      rect.left <= event.clientX &&
      event.clientX <= rect.left + rect.width;

    if (!isInDialog) {
      close();
    }
  };

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <dialog
    v-if="active"
    ref="refDialog"
    class="m-auto! w-full cursor-default overflow-y-auto overscroll-contain rounded-lg bg-white p-4 text-left shadow-xl transition-all print:absolute print:top-0 print:left-0 print:shadow-none"
    :class="[widthGreaterThanSmall, {'overflow-hidden!': isLoading}]"
    :data-test="getTestId('dialog')"
    @cancel.prevent="handleCancel"
  >
    <div class="relative">
      <div v-if="isLoading" class="absolute -inset-4 z-60 backdrop-blur">
        <div class="flex h-full items-center justify-center text-center">
          <MedSpinner size="3xl" :data-test="getTestId('dialog-spinner')" />
        </div>
      </div>

      <div v-if="dialogTitle || $slots.title" class="flex w-full">
        <h3 id="modal-title" class="text-xl leading-6">
          <slot name="title">{{ dialogTitle }}</slot>
        </h3>

        <div class="ml-1">
          <slot name="info-icon" />
        </div>
      </div>

      <div v-if="hasSlotContent === false" :class="{'mt-4': dialogTitle || $slots.title}">
        <p class="text-gray-700">{{ dialogContent }}</p>
      </div>

      <div v-else :class="{'mt-4': dialogTitle || $slots.title}">
        <slot name="content" />
      </div>

      <div
        v-if="hasSlotActions"
        class="mt-4 flex flex-col space-y-3 sm:flex-row-reverse sm:space-y-0 sm:space-x-3 sm:space-x-reverse print:hidden"
      >
        <slot name="actions" />
      </div>

      <div
        v-else-if="dialogType === 'abort'"
        class="mt-4 flex flex-col space-y-3 sm:flex-row-reverse sm:space-y-0 sm:space-x-3 sm:space-x-reverse"
      >
        <MedButton :data-test="getTestId('dialog-close-button')" @click="close">
          {{ cancelLabel }}
        </MedButton>
      </div>

      <div
        v-else-if="dialogType === 'information'"
        class="mt-4 flex flex-col space-y-3 sm:flex-row-reverse sm:space-y-0 sm:space-x-3 sm:space-x-reverse"
      >
        <MedButton
          variant="primary"
          :disabled="primaryButtonDisabled"
          :data-test="getTestId('dialog-ok-button')"
          @click="okButtonClicked"
        >
          {{ primaryLabel }}
        </MedButton>
      </div>

      <div
        v-else-if="dialogType === 'multiActions'"
        class="mt-4 flex flex-col space-y-3 sm:flex-row-reverse sm:space-y-0 sm:space-x-3 sm:space-x-reverse"
      >
        <MedButton
          variant="primary"
          :disabled="primaryButtonDisabled"
          :data-test="getTestId('dialog-primary-button')"
          @click="primaryButtonClicked"
        >
          {{ primaryLabel }}
        </MedButton>

        <template v-if="secondaryLabel">
          <MedButton :data-test="getTestId('dialog-secondary-button')" @click="secondaryButtonClicked">
            {{ secondaryLabel }}
          </MedButton>
        </template>

        <MedButton
          variant="primary"
          flat
          :label="cancelLabel"
          :data-test="getTestId('dialog-close-button')"
          @click="close"
        >
          {{ cancelLabel }}
        </MedButton>
      </div>

      <div v-else-if="dialogType === 'emptyActions'" />

      <div v-else class="mt-4 flex flex-col space-y-3 sm:flex-row-reverse sm:space-y-0 sm:space-x-3 sm:space-x-reverse">
        <MedButton
          variant="primary"
          :disabled="primaryButtonDisabled"
          :data-test="getTestId('dialog-primary-button')"
          @click="primaryButtonClicked"
        >
          {{ primaryLabel }}
        </MedButton>

        <MedButton :data-test="getTestId('dialog-cancel-button')" @click="cancelButtonClicked">
          {{ cancelLabel }}
        </MedButton>
      </div>
    </div>
  </dialog>
</template>
